import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, KeyRound, Lock, ArrowRight, CheckCircle2 } from 'lucide-react';

export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({ email: '', otp: '', newPassword: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSendResetOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Relative path for production and local dev
      const res = await fetch('/api/auth/send-reset-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      const data = await res.json();
      if (data.success) {
        setStep(2);
      } else {
        setError(data.message || 'Email not found.');
      }
    } catch (err) {
      console.error('Send Reset OTP Error:', err);
      setError('Server connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Relative path for production and local dev
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        alert('🎉 Password Reset Successful! Please Login.');
        navigate('/login');
      } else {
        setError(data.message || 'Reset failed.');
      }
    } catch (err) {
      console.error('Reset Password Error:', err);
      setError('Server connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[85vh] flex items-center justify-center bg-[#FAF8F5] px-6 py-12 relative overflow-hidden">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/90 backdrop-blur-xl p-8 sm:p-12 rounded-3xl border border-gray-200/80 shadow-xl max-w-md w-full space-y-6 relative z-10"
      >
        <div className="text-center space-y-2">
          <h2 className="font-serif-title text-3xl font-extrabold text-[#0B2540]">Reset Password</h2>
          <p className="text-xs text-gray-500">
            {step === 1 ? 'Enter registered email to receive OTP.' : 'Enter OTP code and set new password.'}
          </p>
        </div>

        {error && <p className="text-xs font-bold text-red-600 bg-red-50 p-3 rounded-xl border border-red-200 text-center">{error}</p>}

        {step === 1 ? (
          <form onSubmit={handleSendResetOtp} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#0B2540] uppercase tracking-wider mb-1">Email Address</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                <input 
                  type="email" 
                  required
                  placeholder="you@email.com"
                  className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl py-3 pl-10 pr-4 text-xs sm:text-sm focus:outline-none focus:border-[#10B981]" 
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading}
              className="w-full bg-[#10B981] text-white py-3.5 rounded-xl font-bold text-xs sm:text-sm hover:bg-[#0E9F6E] transition shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? 'Sending Code...' : 'Send Reset OTP Code'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#0B2540] uppercase tracking-wider mb-1">Enter 6-Digit OTP</label>
              <div className="relative">
                <KeyRound className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                <input 
                  type="text" 
                  required
                  maxLength="6"
                  placeholder="123456"
                  className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl py-3 pl-10 pr-4 text-center font-mono text-base font-bold tracking-widest focus:outline-none focus:border-[#10B981]" 
                  value={formData.otp}
                  onChange={(e) => setFormData({ ...formData, otp: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#0B2540] uppercase tracking-wider mb-1">New Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                <input 
                  type="password" 
                  required
                  placeholder="••••••••"
                  className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl py-3 pl-10 pr-4 text-xs sm:text-sm focus:outline-none focus:border-[#10B981]" 
                  value={formData.newPassword}
                  onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading}
              className="w-full bg-[#0B2540] text-white py-3.5 rounded-xl font-bold text-xs sm:text-sm hover:bg-[#10B981] transition shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? 'Updating Password...' : 'Reset Password & Login'}
              <CheckCircle2 className="w-4 h-4" />
            </button>
          </form>
        )}

        <p className="text-xs text-center text-gray-500">
          Remember your password? <Link to="/login" className="text-[#10B981] font-bold hover:underline">Back to Login</Link>
        </p>
      </motion.div>
    </div>
  );
}